import axios, { AxiosInstance, AxiosError } from 'axios';

const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:3001';

/**
 * API Client configuration
 */
class ApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/api`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          this.clearToken();
          // Optionally trigger re-login
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Set authentication token
   */
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  /**
   * Clear authentication token
   */
  clearToken(): void {
    this.token = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('graph_token');
    localStorage.removeItem('graph_token_expires');
  }

  /**
   * Set Microsoft Graph token
   */
  setGraphToken(token: string, expiresOn: number): void {
    localStorage.setItem('graph_token', token);
    localStorage.setItem('graph_token_expires', expiresOn.toString());
  }

  /**
   * Get Microsoft Graph token from storage
   */
  getGraphToken(): string | null {
    const token = localStorage.getItem('graph_token');
    const expiresOn = localStorage.getItem('graph_token_expires');
    
    // Check if token is expired
    if (token && expiresOn) {
      const expiryTime = parseInt(expiresOn, 10);
      if (Date.now() < expiryTime) {
        return token;
      }
    }
    
    return null;
  }

  /**
   * Initialize Graph client - fetch and store Microsoft Graph token
   */
  async initializeGraphClient(): Promise<{ success: boolean; token?: string; message?: string }> {
    try {
      // Check if we already have a valid token
      const existingToken = this.getGraphToken();
      if (existingToken) {
        return { success: true, token: existingToken };
      }

      // Fetch new token from backend
      const response = await this.client.get('/auth/graph-token');
      
      if (response.data.success && response.data.token) {
        this.setGraphToken(response.data.token, response.data.expiresOn);
        return { success: true, token: response.data.token };
      }
      
      return { success: false, message: 'Failed to fetch Graph token' };
    } catch (error: any) {
      console.error('Error initializing Graph client:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to initialize Graph client' 
      };
    }
  }

  /**
   * Login user
   */
  async login(email: string, password: string): Promise<any> {
    const response = await this.client.post('/auth/login', { email, password });
    if (response.data.token) {
      this.setToken(response.data.token);
    }
    return response.data;
  }

  /**
   * Match sender email with clients
   */
  async matchClient(senderEmail: string): Promise<any> {
    const response = await this.client.post('/email/match-client', { senderEmail });
    return response.data;
  }

  /**
   * Save email with attachments
   */
  async saveEmail(data: {
    clientId: number;
    email: any;
    attachments: any[];
  }): Promise<any> {
    const response = await this.client.post('/email/save', data);
    return response.data;
  }

  /**
   * Get all clients
   */
  async getClients(): Promise<any> {
    const response = await this.client.get('/clients');
    return response.data;
  }

  /**
   * Get klanten (customers) by email ID
   */
  async getKlantenByEmail(email: string): Promise<any> {
    const response = await this.client.post('/klanten/listklantenbyemailid', { email });
    return response.data;
  }

  /**
   * Save email with attachments to klanten
   */
  async saveEmailAttachments(data: {
    clientId: number;
    email: any;
    attachment: any[];
  }): Promise<any> {
    const response = await this.client.post('/klanten/saveemailattachments', data);
    return response.data;
  }

  /**
   * Get emails for a client
   */
  async getClientEmails(clientId: number, page: number = 1): Promise<any> {
    const response = await this.client.get(`/email/client/${clientId}`, {
      params: { page },
    });
    return response.data;
  }

  /**
   * Get SharePoint folder path for a client
   */
  async getClientSharePointPath(clientId: number): Promise<any> {
    const response = await this.client.get(`/klanten/sharepointpath/${clientId}`);
    return response.data;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
